/**
 * Personel görev tamamlama modalı.
 * mod='tamamla' → ✓ (gerekirse açıklama + foto). mod='yapilamadi' → ✗ (zorunlu açıklama).
 */

import { useEffect, useState } from 'react'
import { X, CheckCircle2, XCircle, Camera } from 'lucide-react'
import {
  gorevTamamla, gorevYapilamadi, gorevFotoYukle, type GorevDoc,
} from '@/lib/services/gorev-service'

interface Props {
  acik: boolean
  gorev: GorevDoc | null
  personelId: string
  tarih: string
  mod: 'tamamla' | 'yapilamadi'
  /** Süresi geçmiş görevi bilinçli olarak geç tamamla (pencere kontrolünü aşar). */
  gecikmeli?: boolean
  onKapat: () => void
  onKaydedildi: () => void
}

export function GorevTamamlaModal({
  acik, gorev, personelId, tarih, mod, gecikmeli, onKapat, onKaydedildi,
}: Props) {
  const [aciklama, setAciklama] = useState('')
  const [fotolar, setFotolar] = useState<File[]>([])
  const [onizlemeler, setOnizlemeler] = useState<string[]>([])
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setAciklama('')
    setFotolar([])
    setOnizlemeler([])
    setHata(null)
  }, [acik, gorev, mod])

  // Önizleme URL'lerini temizle (memory leak önleme)
  useEffect(() => {
    return () => {
      onizlemeler.forEach((u) => URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!acik || !gorev) return null

  const yapilamadiMod = mod === 'yapilamadi'
  const aciklamaGerekli = yapilamadiMod || gorev.aciklamaZorunlu
  const fotoGerekli = !yapilamadiMod && gorev.fotoZorunlu

  function fotoEkle(e: React.ChangeEvent<HTMLInputElement>) {
    const secilen = Array.from(e.target.files ?? [])
    if (secilen.length === 0) return
    setFotolar((prev) => [...prev, ...secilen])
    setOnizlemeler((prev) => [...prev, ...secilen.map((f) => URL.createObjectURL(f))])
    e.target.value = '' // aynı dosya tekrar seçilebilsin
  }

  function fotoSil(idx: number) {
    setOnizlemeler((prev) => {
      const u = prev[idx]
      if (u) URL.revokeObjectURL(u)
      return prev.filter((_, i) => i !== idx)
    })
    setFotolar((prev) => prev.filter((_, i) => i !== idx))
  }

  async function kaydet() {
    setHata(null)
    if (aciklamaGerekli && !aciklama.trim()) {
      setHata('Açıklama zorunlu.')
      return
    }
    if (fotoGerekli && fotolar.length === 0) {
      setHata('Bu görev için fotoğraf zorunlu.')
      return
    }
    setKaydediliyor(true)
    try {
      let fotoUrllar: string[] | undefined
      if (fotolar.length > 0) {
        // Tüm fotoğrafları sırayla yükle
        const urller: string[] = []
        for (const f of fotolar) {
          urller.push(await gorevFotoYukle(gorev.id, personelId, tarih, f))
        }
        fotoUrllar = urller
      }
      if (yapilamadiMod) {
        await gorevYapilamadi(gorev, personelId, aciklama, { tarih, fotoUrllar })
      } else {
        await gorevTamamla(gorev, personelId, {
          tarih,
          aciklama: aciklama.trim() || undefined,
          fotoUrllar,
          gecikmeli,
        })
      }
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {yapilamadiMod
              ? <XCircle className="w-5 h-5 text-red-600" />
              : <CheckCircle2 className="w-5 h-5 text-green-600" />}
            <h2 className="font-bold text-gray-900">
              {yapilamadiMod ? 'Yapılamadı' : 'Görevi Tamamla'}
            </h2>
          </div>
          <button onClick={onKapat} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="font-semibold text-gray-900 text-sm">{gorev.baslik}</p>
            <p className="text-xs text-gray-600 mt-0.5">{gorev.aciklama}</p>
          </div>

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
              {hata}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {yapilamadiMod ? 'Neden yapılamadı?' : 'Açıklama'}
              {aciklamaGerekli && <span className="text-red-500"> *</span>}
              {!aciklamaGerekli && <span className="text-gray-400 text-xs"> (opsiyonel)</span>}
            </label>
            <textarea
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              rows={3}
              placeholder={yapilamadiMod ? 'Örn. Malzeme gelmedi' : 'Not eklemek istersen…'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Fotoğraf
              {fotoGerekli && <span className="text-red-500"> *</span>}
              {!fotoGerekli && <span className="text-gray-400 text-xs"> (opsiyonel, birden fazla eklenebilir)</span>}
            </label>

            {onizlemeler.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {onizlemeler.map((src, idx) => (
                  <div key={idx} className="relative">
                    <img src={src} alt="" className="w-full h-24 rounded-lg object-cover" />
                    <button
                      type="button"
                      onClick={() => fotoSil(idx)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl py-5 cursor-pointer hover:bg-gray-50">
              <Camera className="w-7 h-7 text-gray-400" />
              <span className="text-sm text-gray-500">
                {onizlemeler.length > 0 ? 'Fotoğraf daha ekle' : 'Fotoğraf çek / seç'}
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={fotoEkle}
                className="hidden"
              />
            </label>
            {onizlemeler.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">{onizlemeler.length} fotoğraf eklendi</p>
            )}
          </div>
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onKapat}
            disabled={kaydediliyor}
            className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-semibold hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            onClick={kaydet}
            disabled={kaydediliyor}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50 ${
              yapilamadiMod ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {kaydediliyor
              ? 'Kaydediliyor…'
              : yapilamadiMod ? 'Yapılamadı olarak işaretle' : 'Tamamlandı'}
          </button>
        </div>
      </div>
    </div>
  )
}
